import "../styles/EnterCode.scss";
import {useCallback, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {
  BackButton,
  ArtistBackground,
  Button,
  LanguageSelector,
  Modal,
} from "../components";
import CharacterWithBubble from "../components/CharacterWithBubble";
import {path} from "../router/path";
import {useCode} from "../services/code";
import SessionTimer from "../services/session/components/SessionTimer";
import {ARTIST, getArtistFromPath} from "../utils/artist.utils";

const EnterCode = () => {
  const {t} = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const artist = getArtistFromPath(location.pathname);

  const inputRef = useRef<HTMLInputElement>(null);

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);

  const handleForegroundSessionNone = useCallback(() => {
    navigate(path.waiting(artist), {replace: true});
  }, [navigate, artist]);

  const handleTermsRequired = useCallback(() => {
    setShowTermsModal(true);
  }, []);

  const handleExpiredCode = useCallback(() => {
    navigate(path.expired(artist), {replace: true});
  }, [navigate, artist]);

  const handleServiceUnavailable = useCallback(() => {
    navigate(path.error(artist), {replace: true});
  }, [navigate, artist]);

  const {
    code,
    error,
    ERROR_MESSAGE,
    handleCodeChange,
    handleDelete,
    handleSubmit,
    handleClearSession,
  } = useCode({
    artist,
    onForegroundSessionNone: handleForegroundSessionNone,
    onTermsRequired: handleTermsRequired,
    onExpired: handleExpiredCode,
    onServiceUnavailable: handleServiceUnavailable,
  });

  const handleDeleteKeepFocus = () => {
    handleDelete();
    inputRef.current?.focus();
  };

  const handleBack = () => {
    handleClearSession();
    navigate(path.waiting(artist), {replace: true});
  };

  const handleExpire = () => {
    navigate(path.waiting(artist), {replace: true});
  };

  const handleSubmitCode = async () => {
    try {
      const response = await handleSubmit();
      if (response && response.success) {
        navigate(path.character(artist), {replace: true});
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmitCodeWithTerms = async () => {
    try {
      const response = await handleSubmit({terms_agreed: true});
      if (response && response.success) {
        setShowTermsModal(false);
        navigate(path.character(artist), {replace: true});
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="enter-code-container">
      {showTermsModal && (
        <Modal onClose={() => setShowTermsModal(false)}>
          <div className="terms-modal-container">
            <div className="terms-modal-header">
              <p className="terms-modal-title">{t("terms.title")}</p>
            </div>
            <div className="terms-modal-body">
              <div className="terms-modal-content">
                <div className="terms-modal-content-scroll">
                  <p className="terms-modal-content-title">
                    {t("terms.contentTitle")}
                  </p>
                  <div className="terms-modal-content-body">
                    <span>{t("terms.contentIntro")}</span>
                    <div className="terms-modal-content-body-divider" />
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("terms.contentScript"),
                      }}
                    />
                    <div className="terms-modal-content-body-divider" />
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t("terms.contentNotice"),
                      }}
                    />
                  </div>
                </div>
              </div>
              <label className="terms-modal-agree-wrapper">
                <input
                  type="checkbox"
                  className={`terms-modal-checkbox ${artist}`}
                  checked={termsChecked}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                />
                <span className="terms-modal-agree-label">
                  {t("terms.agreeLabel")}
                </span>
              </label>
            </div>
            <Button
              artist={artist}
              label={t("terms.submitButton")}
              disabled={!termsChecked}
              onClick={handleSubmitCodeWithTerms}
            />
          </div>
        </Modal>
      )}
      <ArtistBackground artist={artist} />
      <div className="enter-code-header">
        <BackButton artist={artist} onClick={handleBack} iconType="close" />
        <LanguageSelector artist={artist} />
      </div>
      <div className="enter-code-content">
        <div className="form-code">
          <h1 className="form-code-title">{t("enterCode.title")}</h1>

          <div className="form-code-content">
            <div className="form-code-character-wrapper">
              <CharacterWithBubble
                content={t("enterCode.notice")}
                artist={artist}
                character={artist === ARTIST.aespa ? "karangi" : "rizuko"}
              />
            </div>
            <div className="form-code-input-wrapper">
              <div className="form-code-input-container">
                <input
                  ref={inputRef}
                  type="text"
                  className="form-code-input-field"
                  placeholder={t("enterCode.placeholder")}
                  value={code}
                  onChange={handleCodeChange}
                />
                {code && (
                  <button
                    className="form-code-delete-button"
                    onPointerDown={(e) => e.preventDefault()}
                    onTouchStart={(e) => e.preventDefault()}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      handleDeleteKeepFocus();
                    }}
                    onClick={handleDeleteKeepFocus}
                    type="button"
                  >
                    <div
                      style={{
                        marginBottom: 2,
                      }}
                    >
                      ×
                    </div>
                  </button>
                )}
              </div>
              <p className="form-code-error-message">
                {error === ERROR_MESSAGE.INVALID &&
                  t("enterCode.error.invalidCode")}
                {error === ERROR_MESSAGE.SESSION_ALIVE &&
                  t("enterCode.error.sessionAlive")}
                {error === ERROR_MESSAGE.UNKNOWN &&
                  t("enterCode.error.unknown")}
                {error === ERROR_MESSAGE.TOO_MANY_FAILED_ATTEMPTS_IN_SESSION &&
                  t("enterCode.error.tooManyFailedAttemptsInSession")}
                {error === ERROR_MESSAGE.TOO_MANY_FAILED_ATTEMPTS_IN_IP &&
                  t("enterCode.error.tooManyFailedAttemptsInIp")}
                {error === ERROR_MESSAGE.ABNORMAL_AUTHENTICATION_ATTEMPTS_IP &&
                  t("enterCode.error.abnormalAuthenticationAttemptsIp")}
              </p>
            </div>
          </div>
          <div className="form-code-submit-wrapper">
            <SessionTimer onExpire={handleExpire} />

            <Button
              artist={artist}
              label={t("enterCode.verifyButton")}
              onClick={handleSubmitCode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterCode;
